"use client";

import { createBrowserSupabase } from "@/lib/supabase/browser";
import { describeSettingsWriteError } from "@/lib/settingsWriteError";
import { getClientStoreId } from "@/lib/supabase/storeContextClient";
import { withStorePrefix } from "@/lib/supabase/storagePaths";
import {
  DEFAULT_LOYALTY_SETTINGS,
  mapLoyaltySettings,
  mapLoyaltyTxnRow,
  mapProgramRow,
  mapProgramSaveResult,
  mapTiers,
  sortTiers,
  type LoyaltyTier,
  mapClawbackShortfallRow,
  describeProgramDeleteError,
  isProgramRunning,
  mapRedemptionRow,
  mapRewardRow,
  mapTargetRow,
  programDraftToPayload,
  rewardEditPayload,
  rewardInsertPayload,
  rewardStockChanged,
  type LoyaltyPointsBalance,
  type LoyaltyProgram,
  type LoyaltyClawbackShortfall,
  type LoyaltyProgramDraft,
  type LoyaltyProgramSaveResult,
  type LoyaltyRedemption,
  type LoyaltyReward,
  type LoyaltyRewardDraft,
  type LoyaltySettings,
  type LoyaltyTxn,
} from "@/lib/loyalty";
import {
  CUSTOMER_POINTS_LIMIT,
  EMPTY_INVOICE_LOYALTY_POINTS,
  EMPTY_LOYALTY_POTENTIAL,
  mapCustomerPointsRows,
  mapInvoiceLoyaltyPoints,
  mapLoyaltyPotential,
  type CustomerPointsRow,
  type CustomerPointsSort,
  type InvoiceLoyaltyPoints,
  type LoyaltyPotential,
} from "@/lib/loyaltyPotential";
import { formatInvoiceNo } from "@/lib/invoices";

/**
 * Kunci yang DIBACA harus sama dengan yang DITULIS `saveLoyaltySettings`. Dulu
 * dua kunci kedaluwarsa tak ikut dibaca: formulir admin selalu menampilkan
 * "mati / 180 hari", dan Simpan Setelan (untuk perubahan apa pun) menuliskannya
 * balik — mematikan kedaluwarsa yang sudah diaktifkan tanpa terlihat.
 */
const LOYALTY_SETTING_KEYS = [
  "loyalty_saldo_enabled",
  "loyalty_rupiah_per_point",
  "loyalty_min_redeem_points",
  "loyalty_points_expiry_enabled",
  "loyalty_points_expiry_days",
] as const;

// ════════════════════════════════════════════════════════════
// PROGRAM (admin)
// ════════════════════════════════════════════════════════════

/** Daftar SEMUA program (admin lihat aktif & nonaktif) + target tergabung. */
export async function fetchLoyaltyPrograms(): Promise<LoyaltyProgram[]> {
  const db = createBrowserSupabase();
  if (!db) return [];

  const { data: programs, error } = await db
    .from("loyalty_programs")
    .select(
      "id, name, description, is_active, starts_at, ends_at, product_type_scope, basis, target_type, threshold, points_value, created_at",
    )
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);

  const rows = (programs as Record<string, unknown>[] | null) ?? [];
  const ids = rows.map((r) => String(r.id));
  const targetsByProgram = new Map<string, ReturnType<typeof mapTargetRow>[]>();

  if (ids.length > 0) {
    const { data: targets, error: tErr } = await db
      .from("loyalty_program_targets")
      .select("program_id, kind, product_id, name")
      .in("program_id", ids);
    if (tErr) throw new Error(tErr.message);
    for (const t of (targets as Record<string, unknown>[] | null) ?? []) {
      const pid = String(t.program_id);
      const list = targetsByProgram.get(pid) ?? [];
      list.push(mapTargetRow(t));
      targetsByProgram.set(pid, list);
    }
  }

  return rows.map((r) => mapProgramRow(r, targetsByProgram.get(String(r.id)) ?? []));
}

/**
 * Simpan program + target (atomik via RPC admin_upsert_loyalty_program).
 *
 * Mig 6810 (bughunt F7-2): aturan program yang sudah memberi poin dikunci —
 * server menolak perubahannya kecuali `draft.applyRetro` (terapkan mundur,
 * hanya-naik). `dryRun` menjalankan simpan + terapkan mundur lalu
 * membatalkannya di server, memulangkan ringkasan untuk konfirmasi admin.
 */
export async function saveLoyaltyProgram(
  draft: LoyaltyProgramDraft,
  opts: { dryRun?: boolean } = {},
): Promise<LoyaltyProgramSaveResult> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");
  const { program, targets } = programDraftToPayload(draft);
  const { data, error } = await db.rpc("admin_upsert_loyalty_program", {
    p_program: opts.dryRun ? { ...program, dryRun: true } : program,
    p_targets: targets,
  });
  if (error) throw new Error(error.message);
  return mapProgramSaveResult(data);
}

export async function deleteLoyaltyProgram(id: string): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");
  const { error } = await db.from("loyalty_programs").delete().eq("id", id);
  if (error) throw new Error(describeProgramDeleteError(error.message));
}

export async function setProgramActive(id: string, active: boolean): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");
  const { error } = await db
    .from("loyalty_programs")
    .update({ is_active: active, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw new Error(error.message);
}

/** Program yang sedang AKTIF (storefront). Periode difilter di client. */
export async function fetchActivePrograms(): Promise<LoyaltyProgram[]> {
  const db = createBrowserSupabase();
  if (!db) return [];
  // MULTI-TENANT — FAIL-CLOSED. Beranda bisa dibuka tamu (anon lolos cabang
  // escape RLS) → tanpa filter store_id strip poin menampilkan program toko
  // lain. Target di bawah sudah ter-scope lewat id program.
  //
  // ⛔ Toko tak ter-resolusi ≠ "pakai toko mana saja" (konvensi CLAUDE.md, PR
  // #571): storeId null (apex platform / registry gagal) → TANPA program, bukan
  // program milik tenant sembarang. Sekelas dengan kebocoran popup lintas-tenant
  // di `fetchActivePopups`.
  const storeId = getClientStoreId();
  if (!storeId) return [];

  const { data, error } = await db
    .from("loyalty_programs")
    .select(
      "id, name, description, is_active, starts_at, ends_at, product_type_scope, basis, target_type, threshold, points_value, created_at",
    )
    .eq("is_active", true)
    .eq("store_id", storeId)
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);

  const rows = (data as Record<string, unknown>[] | null) ?? [];
  const ids = rows.map((r) => String(r.id));
  const byProgram = new Map<string, ReturnType<typeof mapTargetRow>[]>();
  if (ids.length > 0) {
    const { data: targets } = await db
      .from("loyalty_program_targets")
      .select("program_id, kind, product_id, name")
      .in("program_id", ids);
    for (const t of (targets as Record<string, unknown>[] | null) ?? []) {
      const pid = String(t.program_id);
      const list = byProgram.get(pid) ?? [];
      list.push(mapTargetRow(t));
      byProgram.set(pid, list);
    }
  }
  return rows.map((r) => mapProgramRow(r, byProgram.get(String(r.id)) ?? []));
}

/**
 * Apakah toko ini punya program poin yang SEDANG berjalan — penentu bagian
 * "Poin" di kartu saldo beranda. Satu query ringan (tanpa target), periode
 * dinilai `isProgramRunning` seperti `fetchActivePrograms`.
 * FAIL-CLOSED: toko tak ter-resolusi → false, bukan program toko sembarang.
 */
export async function fetchHasRunningProgram(): Promise<boolean> {
  const db = createBrowserSupabase();
  if (!db) return false;
  const storeId = getClientStoreId();
  if (!storeId) return false;

  const { data, error } = await db
    .from("loyalty_programs")
    .select("is_active, starts_at, ends_at")
    .eq("is_active", true)
    .eq("store_id", storeId);
  if (error) throw new Error(error.message);
  return ((data as Record<string, unknown>[] | null) ?? []).some((r) =>
    isProgramRunning({
      isActive: !!r.is_active,
      startsAt: (r.starts_at as string) ?? null,
      endsAt: (r.ends_at as string) ?? null,
    }),
  );
}

// ════════════════════════════════════════════════════════════
// REWARD (hadiah/voucher)
// ════════════════════════════════════════════════════════════

export async function fetchLoyaltyRewards(activeOnly = false): Promise<LoyaltyReward[]> {
  const db = createBrowserSupabase();
  if (!db) return [];
  let q = db
    .from("loyalty_rewards")
    .select("id, name, description, points_cost, stock, image_url, is_active, created_at")
    .order("created_at", { ascending: false });
  if (activeOnly) q = q.eq("is_active", true);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return ((data as Record<string, unknown>[] | null) ?? []).map(mapRewardRow);
}

export async function saveReward(draft: LoyaltyRewardDraft): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");

  if (!draft.id) {
    const { error } = await db.from("loyalty_rewards").insert(rewardInsertPayload(draft));
    if (error) throw new Error(error.message);
    return;
  }

  // F4-2: penyimpanan biasa TIDAK menuliskan `stock`. Ia besaran hidup yang
  // didekremen `redeem_points_reward` dan direstok `admin_reject_redemption`;
  // angka di formulir sudah basi begitu satu klaim masuk, dan menuliskannya
  // balik menghapus dekremen klaim itu → hadiah bisa diklaim melebihi stok.
  const { error } = await db
    .from("loyalty_rewards")
    .update({ ...rewardEditPayload(draft), updated_at: new Date().toISOString() })
    .eq("id", draft.id);
  if (error) throw new Error(error.message);

  if (!rewardStockChanged(draft)) return;

  // Perubahan stok yang DISENGAJA: ditulis terpisah dengan penjaga bentrok —
  // hanya kena bila stoknya masih sama seperti saat formulir dibuka.
  const base = db.from("loyalty_rewards").update({ stock: draft.stock }).eq("id", draft.id);
  const guarded =
    draft.originalStock === null ? base.is("stock", null) : base.eq("stock", draft.originalStock);
  const { data, error: stockErr } = await guarded.select("id");
  if (stockErr) throw new Error(stockErr.message);
  if (!data || data.length === 0) {
    throw new Error(
      "Stok hadiah sudah berubah sejak formulir dibuka (kemungkinan ada klaim baru). " +
        "Perubahan lain sudah tersimpan — muat ulang halaman lalu atur stoknya lagi.",
    );
  }
}

export async function deleteReward(id: string): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");
  const { error } = await db.from("loyalty_rewards").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

/** Upload gambar hadiah ke bucket publik product-media (pola uploadQrisImage). */
export async function uploadRewardImage(file: File): Promise<string> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");
  const ts = Date.now();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const storagePath = await withStorePrefix(
    db,
    `loyalty-rewards/${ts}_${safeName}`
  );
  const { data: uploaded, error } = await db.storage
    .from("product-media")
    .upload(storagePath, file, { upsert: false, cacheControl: "31536000" });
  if (error) throw new Error(`Gagal upload gambar: ${error.message}`);
  const { data: urlData } = db.storage.from("product-media").getPublicUrl(uploaded.path);
  return urlData.publicUrl;
}

// ════════════════════════════════════════════════════════════
// REDEMPTION (klaim)
// ════════════════════════════════════════════════════════════

export type RedemptionWithCustomer = LoyaltyRedemption & {
  customerName: string;
  customerPhone: string;
};

function mapRedemptionWithCustomer(row: Record<string, unknown>): RedemptionWithCustomer {
  const base = mapRedemptionRow(row);
  const c = (row.customer ?? {}) as Record<string, unknown>;
  const name =
    (typeof c.store_name === "string" && c.store_name.trim()) ||
    (typeof c.full_name === "string" && c.full_name) ||
    "Pelanggan";
  return {
    ...base,
    customerName: String(name),
    customerPhone: typeof c.phone === "string" ? c.phone : "",
  };
}

/** Klaim hadiah PENDING (admin Penuhi/Tolak). */
export async function fetchPendingRedemptions(): Promise<RedemptionWithCustomer[]> {
  const db = createBrowserSupabase();
  if (!db) return [];
  const { data, error } = await db
    .from("loyalty_redemptions")
    .select(
      "id, customer_id, type, reward_id, reward_name, points_spent, rupiah_credited, status, note, created_at, fulfilled_at, customer:customers(full_name, store_name, phone)",
    )
    .eq("type", "reward")
    .eq("status", "pending")
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return ((data as Record<string, unknown>[] | null) ?? []).map(mapRedemptionWithCustomer);
}

export async function fulfillRedemption(id: string): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");
  const { error } = await db.rpc("admin_fulfill_redemption", { p_redemption_id: id });
  if (error) throw new Error(error.message);
}

export async function rejectRedemption(id: string, note?: string): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");
  const { error } = await db.rpc("admin_reject_redemption", {
    p_redemption_id: id,
    p_note: note ?? null,
  });
  if (error) throw new Error(error.message);
}

/**
 * Jejak penarikan poin yang tak tertutup (bughunt F4-1, tabel mig 5270/5310).
 * RLS sudah men-scope ke toko admin; klien tak punya hak DML atas tabel ini.
 */
export async function fetchClawbackShortfalls(limit = 30): Promise<LoyaltyClawbackShortfall[]> {
  const db = createBrowserSupabase();
  if (!db) return [];
  const { data, error } = await db
    .from("loyalty_clawback_shortfalls")
    .select(
      "id, invoice_id, customer_id, points_due, points_taken, points_short, points_via_saldo, rupiah_reclaimed, points_unrecoverable, created_at",
    )
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return ((data as Record<string, unknown>[] | null) ?? []).map(mapClawbackShortfallRow);
}

/** Klaim/penukaran milik pembeli (riwayat). */
export async function fetchMyRedemptions(customerId: string): Promise<LoyaltyRedemption[]> {
  const db = createBrowserSupabase();
  if (!db) return [];
  const { data, error } = await db
    .from("loyalty_redemptions")
    .select(
      "id, customer_id, type, reward_id, reward_name, points_spent, rupiah_credited, status, note, created_at, fulfilled_at",
    )
    .eq("customer_id", customerId)
    .order("created_at", { ascending: false })
    .limit(50);
  if (error) throw new Error(error.message);
  return ((data as Record<string, unknown>[] | null) ?? []).map(mapRedemptionRow);
}

// ════════════════════════════════════════════════════════════
// SETELAN PENUKARAN (site_settings)
// ════════════════════════════════════════════════════════════

export async function fetchLoyaltySettings(): Promise<LoyaltySettings> {
  const db = createBrowserSupabase();
  if (!db) return { ...DEFAULT_LOYALTY_SETTINGS };
  const { data, error } = await db
    .from("site_settings")
    .select("key, value")
    .in("key", LOYALTY_SETTING_KEYS as unknown as string[]);
  if (error) throw new Error(error.message);
  return mapLoyaltySettings((data as { key: string; value: string | null }[] | null) ?? []);
}

export async function saveLoyaltySettings(settings: LoyaltySettings): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");
  const rows = [
    { key: "loyalty_saldo_enabled", value: settings.saldoEnabled ? "true" : "false" },
    // F4-4: null = belum diatur → simpan string kosong, bukan "null"/"0".
    {
      key: "loyalty_rupiah_per_point",
      value: settings.rupiahPerPoint === null ? "" : String(settings.rupiahPerPoint),
    },
    { key: "loyalty_min_redeem_points", value: String(settings.minRedeemPoints) },
    { key: "loyalty_points_expiry_enabled", value: settings.expiryEnabled ? "true" : "false" },
    { key: "loyalty_points_expiry_days", value: String(settings.expiryDays) },
  ];
  const { error } = await db.from("site_settings").upsert(rows, { onConflict: "store_id,key" });
  if (error) throw new Error(describeSettingsWriteError(error));
}

/**
 * Tier poin (site_settings `loyalty_tiers`, JSON). Baris tak ada → DEFAULT_TIERS;
 * `[]` → tier dimatikan (lihat `mapTiers`). Galat baca DILEMPAR, tidak lagi
 * ditelan jadi DEFAULT_TIERS: di editor admin itu menyajikan tier bawaan yang
 * terlihat sah dan satu klik Simpan Tier menimpa daftar asli; di storefront
 * pemanggil menangkapnya dan menyembunyikan badge.
 */
export async function fetchLoyaltyTiers(): Promise<LoyaltyTier[]> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");
  const { data, error } = await db
    .from("site_settings")
    .select("value")
    .eq("key", "loyalty_tiers")
    .maybeSingle();
  if (error) throw new Error(error.message);
  return mapTiers((data as { value?: string | null } | null)?.value ?? null);
}

export async function saveLoyaltyTiers(tiers: LoyaltyTier[]): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");
  const clean = sortTiers(tiers);
  const { error } = await db
    .from("site_settings")
    .upsert([{ key: "loyalty_tiers", value: JSON.stringify(clean) }], { onConflict: "store_id,key" });
  if (error) throw new Error(describeSettingsWriteError(error));
}

// ════════════════════════════════════════════════════════════
// POIN PELANGGAN (saldo + riwayat)
// ════════════════════════════════════════════════════════════

/**
 * Saldo poin satu pelanggan. Baris tak ada = belum pernah dapat poin (0).
 * Galat baca DILEMPAR (bughunt F7-6): dulu ditelan jadi "Poin 0" di kartu
 * saldo beranda, Akun, dan /poin — pemanggil menampilkan "•••"/"gagal dimuat".
 */
export async function fetchCustomerPoints(customerId: string): Promise<LoyaltyPointsBalance> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");
  const { data, error } = await db
    .from("loyalty_points")
    .select("points, lifetime_points")
    .eq("customer_id", customerId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  const row = data as { points?: number; lifetime_points?: number } | null;
  return {
    points: Number(row?.points ?? 0),
    lifetimePoints: Number(row?.lifetime_points ?? 0),
  };
}

/** Ringkas poin pembeli yang akan hangus dalam `withinDays` ke depan (mig 143). */
export async function fetchMyExpiringPoints(
  customerId: string,
  withinDays = 30,
): Promise<{ points: number; soonest: string | null }> {
  const db = createBrowserSupabase();
  if (!db) return { points: 0, soonest: null };
  const now = new Date();
  const until = new Date(now.getTime() + withinDays * 86_400_000).toISOString();
  const { data } = await db
    .from("loyalty_point_lots")
    .select("points_remaining, expires_at")
    .eq("customer_id", customerId)
    .gt("points_remaining", 0)
    .not("expires_at", "is", null)
    .gt("expires_at", now.toISOString())
    .lte("expires_at", until)
    .order("expires_at", { ascending: true });
  const rows = (data as { points_remaining?: number; expires_at?: string }[] | null) ?? [];
  const points = rows.reduce((s, r) => s + Number(r.points_remaining ?? 0), 0);
  return { points, soonest: rows.length > 0 ? (rows[0].expires_at ?? null) : null };
}

export type PointHistoryOpts = {
  offset?: number;
  limit?: number;
  startIso?: string | null; // inklusif
  endIso?: string | null; // eksklusif
};

export async function fetchCustomerPointHistory(
  customerId: string,
  opts: PointHistoryOpts = {},
): Promise<LoyaltyTxn[]> {
  const db = createBrowserSupabase();
  if (!db) return [];
  const offset = opts.offset ?? 0;
  const limit = opts.limit ?? 20;
  let q = db
    .from("loyalty_transactions")
    .select("id, type, direction, points, balance_after, program_id, reference_id, note, created_at")
    .eq("customer_id", customerId)
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1);
  if (opts.startIso) q = q.gte("created_at", opts.startIso);
  if (opts.endIso) q = q.lt("created_at", opts.endIso);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return ((data as Record<string, unknown>[] | null) ?? []).map(mapLoyaltyTxnRow);
}

/**
 * Kode nota ("TGH…" / "#12") untuk baris riwayat poin yang merujuk nota.
 * Hanya nota MILIK pembeli ini (filter `customer_id` + RLS "Customers read own
 * invoices"); id yang tak kembali berarti bukan notanya → barisnya tak ditautkan.
 */
export async function fetchMyInvoiceLabels(
  customerId: string,
  invoiceIds: string[],
): Promise<Record<string, string>> {
  const db = createBrowserSupabase();
  if (!db || invoiceIds.length === 0) return {};
  const { data, error } = await db
    .from("invoices")
    .select("id, invoice_no, invoice_code")
    .eq("customer_id", customerId)
    .in("id", invoiceIds);
  if (error) throw new Error(error.message);
  const out: Record<string, string> = {};
  for (const r of (data as Record<string, unknown>[] | null) ?? []) {
    const no = Number(r.invoice_no);
    out[String(r.id)] = formatInvoiceNo(
      Number.isFinite(no) ? no : null,
      typeof r.invoice_code === "string" ? r.invoice_code : null,
    );
  }
  return out;
}

/**
 * Potensi poin pembeli yang login (mig 6640): poin yang AKAN masuk saat nota /
 * barangnya lunas. Toko & pelanggan di-resolve DI RPC (current_store_id() +
 * auth.uid(), fail-closed → kosong), jadi tidak ada filter store_id di sini.
 */
export async function fetchMyLoyaltyPotential(): Promise<LoyaltyPotential> {
  const db = createBrowserSupabase();
  if (!db) return EMPTY_LOYALTY_POTENTIAL;
  const { data, error } = await db.rpc("get_my_loyalty_potential");
  if (error) throw new Error(error.message);
  return mapLoyaltyPotential(data);
}

/** Poin satu nota milik pembeli (sudah masuk / akan masuk bila lunas). Mig 6640. */
export async function fetchMyInvoiceLoyaltyPoints(
  invoiceId: string,
): Promise<InvoiceLoyaltyPoints> {
  const db = createBrowserSupabase();
  if (!db) return EMPTY_INVOICE_LOYALTY_POINTS;
  const { data, error } = await db.rpc("get_my_invoice_loyalty_points", {
    p_invoice_id: invoiceId,
  });
  if (error) throw new Error(error.message);
  return mapInvoiceLoyaltyPoints(data);
}

/** Penukaran poin → saldo (pembeli). RPC redeem_points_to_saldo. */
export async function redeemPointsToSaldo(points: number): Promise<{
  pointsAfter: number;
  saldoAfter: number;
  rupiah: number;
}> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");
  const { data, error } = await db.rpc("redeem_points_to_saldo", { p_points: points });
  if (error) throw new Error(error.message);
  const r = (data ?? {}) as Record<string, unknown>;
  return {
    pointsAfter: Number(r.pointsAfter ?? 0),
    saldoAfter: Number(r.saldoAfter ?? 0),
    rupiah: Number(r.rupiah ?? 0),
  };
}

/** Penukaran poin → hadiah (pembeli). RPC redeem_points_reward. */
export async function redeemPointsReward(rewardId: string): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");
  const { error } = await db.rpc("redeem_points_reward", { p_reward_id: rewardId });
  if (error) throw new Error(error.message);
}

// ════════════════════════════════════════════════════════════
// ADMIN: kelola poin pelanggan
// ════════════════════════════════════════════════════════════

/** Baris tab admin Poin Pelanggan (poin, total, potensi) — mig 6670. */
export type CustomerPointsSummary = CustomerPointsRow;

/**
 * Daftar poin pelanggan untuk admin, TERURUT di server (RPC
 * `admin_loyalty_customer_points`, izin `saldo.poin` + toko admin):
 * `points` = poin terbanyak lalu potensi; `potential` = sebaliknya.
 * Tanpa pencarian → pelanggan yang punya poin / riwayat poin / potensi;
 * dengan pencarian (nama, nama toko, no HP) → semua yang cocok.
 */
export async function fetchCustomerPointsRanking(
  query: string,
  sort: CustomerPointsSort = "points",
): Promise<CustomerPointsSummary[]> {
  const db = createBrowserSupabase();
  if (!db) return [];
  const { data, error } = await db.rpc("admin_loyalty_customer_points", {
    p_query: query.trim() || null,
    p_sort: sort,
    p_limit: CUSTOMER_POINTS_LIMIT,
  });
  if (error) throw new Error(error.message);
  return mapCustomerPointsRows(data);
}

export async function adminAdjustPoints(
  customerId: string,
  points: number,
  direction: "in" | "out",
  note: string,
): Promise<number> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");
  const { data, error } = await db.rpc("admin_adjust_loyalty_points", {
    p_customer_id: customerId,
    p_points: points,
    p_direction: direction,
    p_note: note,
  });
  if (error) throw new Error(error.message);
  return Number(data ?? 0);
}

// ════════════════════════════════════════════════════════════
// PEMILIH PRODUK (target program)
// ════════════════════════════════════════════════════════════

export type ProductOption = { id: string; name: string; brand: string | null };

/** Cari produk untuk target "produk tertentu". */
export async function searchProductOptions(query: string): Promise<ProductOption[]> {
  const db = createBrowserSupabase();
  if (!db) return [];
  const q = query.trim();
  let pq = db
    .from("products")
    .select("id, name, brand")
    .order("name", { ascending: true })
    .limit(20);
  if (q) {
    const safe = q.replace(/[%,()]/g, " ");
    pq = pq.ilike("name", `%${safe}%`);
  }
  const { data, error } = await pq;
  if (error) throw new Error(error.message);
  return ((data as Record<string, unknown>[] | null) ?? []).map((r) => ({
    id: String(r.id),
    name: typeof r.name === "string" ? r.name : "",
    brand: (r.brand as string) ?? null,
  }));
}
